import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Pill } from '../sso-workspace/pill'
import { formatAgo, plural } from '../sso-workspace/format'
import { actorView, categoryLabel, categoryTone, exactUTC, viaText } from './entry-format'
import { storyView } from './entry-story'
import { FactChips, Headline } from './place-chip'
import { AppChip, appChipView } from './app-chip'
import { EntryDetails, withinPatch } from './audit-entry'
import './audit.scss'

// One story of the timeline (owner 2026-10-09): entries content keyed as
// one story (a build's publish across its init and complete requests; one
// request about one course), shown as one entry -- who, the story's
// headline with its course and folder (its version and file linked to
// their pages), what it came to (current v13 → v14, upload complete · 207
// MB), and "N changes", which lists each change in plain words, oldest
// first ("..., file fingerprint saved (SHA-256)"), each with its own
// Details.
//
// entries: the story's entries, newest first (the timeline's order). A
// story of one is an AuditEntry, never this.

// The story's category: Publishing when any change is publishing, else the
// newest entry's.
const categoryOf = entries => (entries.some(e => e.category === 'publishing') ? 'publishing' : entries[0].category)

const StoryLine = ({ entry, text, sys, caps, onPivot }) => {
  const [open, setOpen] = useState(false)
  const [detail, setDetail] = useState(null)
  return (
    <li className="au-story-line">
      <span>{text}</span>
      {' · '}
      <button type="button" className="btn btn-link btn-sm p-0 au-linkbtn" aria-expanded={open} aria-label={`${open ? 'Hide details' : 'Details'}: ${text}`} onClick={() => setOpen(o => !o)}>
        {open ? 'Hide details' : 'Details'}
      </button>
      {open && <EntryDetails entry={entry} detail={detail} onLoaded={setDetail} sys={sys} caps={caps} onPivot={onPivot} />}
    </li>
  )
}

export const AuditStory = ({ entries, sys = false, caps = {}, onPivot }) => {
  const [open, setOpen] = useState(false)
  const newest = entries[0]
  const view = storyView(entries)
  const who = actorView(newest.actor, { sys })
  const via = viaText(newest, { sys })
  // the story's chip: the newest entry's app, with the versions any of the
  // story's entries know (a publish's build, a header)
  const app = appChipView(newest, { automatic: who.automatic || who.unattributed, related: entries })
  const category = categoryOf(entries)
  const onWithin = onPivot ? p => onPivot(withinPatch(p)) : null
  const count = plural(entries.length, 'change')

  return (
    <li className="au-ev au-story">
      <div className={`au-av${who.automatic || who.unattributed || who.support ? ' au-av-sys' : ''}`} aria-hidden="true">{who.initials}</div>
      <div className="au-body">
        <div className="au-line">
          {who.href ? <Link className="au-who" to={who.href}>{who.text}</Link> : <b className="au-who">{who.text}</b>}
          {via && <>{' '}<span className="sw-hint au-note">{via}</span></>}
          {who.note && <>{' · '}<span className="sw-hint au-note">{who.note}</span></>}
          {' '}
          {view.headline
            ? <Headline segments={view.headline} sys={sys} onWithin={onWithin} />
            : <span className="au-sentence">{newest.sentence}</span>}
        </div>
        <FactChips chips={view.chips} />
        <div className="au-meta">
          <time dateTime={newest.occurredAt} title={exactUTC(newest.occurredAt)}>{formatAgo(newest.occurredAt)}</time>
          {category && <Pill tone={categoryTone(category)}>{categoryLabel(category)}</Pill>}
          {who.automatic && <Pill title="Made by an automatic job or integration">automatic</Pill>}
          {app && <span className="au-meta-break" aria-hidden="true" />}
          {app && <AppChip view={app} />}
          <button type="button" className="btn btn-link btn-sm p-0 au-linkbtn au-story-toggle" aria-expanded={open} onClick={() => setOpen(o => !o)}>
            {count}<span className={`au-caret${open ? ' au-caret-open' : ''}`} aria-hidden="true" />
          </button>
        </div>
        {open &&
          <ol className="au-story-lines" aria-label={`The ${count}`}>
            {view.lines.map(l => <StoryLine key={l.entry.id} entry={l.entry} text={l.text} sys={sys} caps={caps} onPivot={onPivot} />)}
          </ol>}
      </div>
    </li>
  )
}
